import { HueTintAndOutlineFXPipeline, pixelCoordToTileCoord, tileCoordToPixelCoord } from "@latticexyz/phaserx";
import { removeComponent } from "@latticexyz/recs";
import { Coord } from "@latticexyz/utils";
import { useEffect, useMemo, useState } from "react";
import { merge } from "rxjs";
import { UnitTypeAnimations } from "../../../layers/Renderer/Phaser/phaserConstants";
import { RenderDepth } from "../../../layers/Renderer/Phaser/types";
import { useMUD } from "../../../store";
import { useCurrentPlayer } from "../hooks/useCurrentPlayer";
import { BuildData } from "./types";

export const BuildSprites = ({
  buildData,
  position,
  stopBuilding,
}: {
  position: Coord;
  buildData: BuildData;
  stopBuilding: () => void;
}) => {
  const {
    phaserLayer: {
      components: { HoverHighlight },
      scenes: {
        Main: {
          phaserScene,
          objectPool,
          input,
          maps: {
            Main: { tileWidth, tileHeight },
          },
        },
      },
      api: {
        mapInteraction: { disableMapInteraction, enableMapInteraction },
        drawTileHighlight,
      },
    },
    headlessLayer: {
      api: { buildAt },
    },
    localLayer: { singletonEntity },
  } = useMUD();

  const currentPlayer = useCurrentPlayer();

  const [tileHighlightIds, setTileHighlightIds] = useState<string[]>([]);

  const buildPositions = useMemo(
    () => [
      { ...position, x: position.x + 1 },
      { ...position, x: position.x - 1 },
      { ...position, y: position.y + 1 },
      { ...position, y: position.y - 1 },
    ],
    [position]
  );

  useEffect(() => {
    return () => {
      for (let i = 0; i < tileHighlightIds.length; i++) {
        objectPool.remove(tileHighlightIds[i]);
      }
    };
  }, [objectPool, tileHighlightIds]);

  useEffect(() => {
    const unitType = buildData.unitType;
    const spriteAnimation = UnitTypeAnimations[unitType];

    const sprite = phaserScene.add.sprite(0, 0, "");
    sprite.play(spriteAnimation);
    sprite.setDepth(RenderDepth.UI1);
    sprite.setOrigin(0, 0);
    sprite.setPipeline(HueTintAndOutlineFXPipeline.KEY);
    sprite.setPipelineData("hueTint", currentPlayer?.playerColor);

    const unitPreviewSub = input.pointermove$.subscribe(({ pointer }) => {
      const tile = pixelCoordToTileCoord(
        {
          x: pointer.worldX,
          y: pointer.worldY,
        },
        tileWidth,
        tileHeight
      );

      if (sprite) {
        const previewPosition = tileCoordToPixelCoord(tile, tileWidth, tileHeight);
        sprite.setPosition(previewPosition.x, previewPosition.y);
        if (!buildPositions.find((bp) => bp.x === tile.x && bp.y === tile.y)) {
          sprite.setTint(0x666666);
        } else {
          sprite.clearTint();
        }
      }
    });

    return () => {
      unitPreviewSub.unsubscribe();
      sprite?.destroy();
    };
  }, [
    buildData.unitType,
    buildPositions,
    currentPlayer?.playerColor,
    input.pointermove$,
    phaserScene.add,
    tileHeight,
    tileWidth,
  ]);

  useEffect(() => {
    disableMapInteraction();
    removeComponent(HoverHighlight, singletonEntity);

    for (let i = 0; i < buildPositions.length; i++) {
      setTileHighlightIds((ids) => [...ids, `build-position-${i}`]);
      drawTileHighlight(`build-position-${i}`, buildPositions[i], 0xffff00);
    }

    const buildClickSub = merge(input.click$, input.rightClick$).subscribe((pointer) => {
      const tile = pixelCoordToTileCoord(
        {
          x: pointer.worldX,
          y: pointer.worldY,
        },
        tileWidth,
        tileHeight
      );
      for (const buildPosition of buildPositions) {
        if (tile.x === buildPosition.x && tile.y === buildPosition.y) {
          buildAt(buildData.factory, buildData.prototypeId, buildPosition);
        }
      }

      stopBuilding();
      enableMapInteraction();
    });

    return () => {
      buildClickSub.unsubscribe();
      enableMapInteraction();
    };
    // literally only run this on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <></>;
};
